import waypoints from './waypoints';
import { type PowerNeedVehicle } from '@/app/services/solution/types/get-power-need';
import { type ChargingScheduleVehicle } from '@/app/services/solution/types/get-charging-schedule';

function timeToMinutes(time: string): number {
  const [h, m] = time.split(':').map(Number);
  return h * 60 + m;
}

function generateTimeSeries(): string[] {
  const result: string[] = [];
  for (let index = 0; index < 289; index++) {
    const hours = String(Math.floor((index * 5) / 60)).padStart(2, '0');
    const minutes = String((index * 5) % 60).padStart(2, '0');
    result.push(`${hours}:${minutes}`);
  }
  return result;
}

type TimeRangeTuple = [startTime: string, endTime: string];

function buildChargingRanges(schedule: { type: string; time: string }[]): TimeRangeTuple[] {
  const ranges: TimeRangeTuple[] = [];

  for (let index = 0; index < schedule.length - 1; index++) {
    const current = schedule[index];

    if (current.type === 'Charging') {
      const endIndex = schedule.slice(index + 1).findIndex((item) => item.type !== 'Charging');
      const endTime = endIndex === -1 ? '24:00' : schedule[index + 1 + endIndex].time;
      ranges.push([current.time, endTime]);
    }
  }

  // Handle final charging period if last item is Charging
  const last = schedule.at(-1);
  if (last?.type === 'Charging') {
    ranges.push([last.time, '24:00']);
  }

  return ranges;
}

export function getPowerNeedVehicles(
  chargingScheduleVehicles: ChargingScheduleVehicle[],
  powerPerVehicle = 85,
): PowerNeedVehicle[] {
  const timeSeries = generateTimeSeries();

  return chargingScheduleVehicles.map((vehicle) => {
    const ranges = buildChargingRanges(vehicle.chargingScheduleItems);

    const powerNeedItems = timeSeries.map((time) => {
      const tMin = timeToMinutes(time);
      const charging = ranges.some(([start, end]) => {
        return tMin >= timeToMinutes(start) && (tMin < timeToMinutes(end) || end === '24:00');
      });

      return { time, powerNeed: charging ? powerPerVehicle : 0 };
    });

    const waypoint = waypoints.find((waypoint) => waypoint.id === vehicle.waypointId);

    return {
      vehicleId: vehicle.vehicleId,
      waypointId: vehicle.waypointId,
      waypointName: waypoint?.name ?? 'Unknown',
      waypointType: waypoint?.type ?? 'Depot',
      maxCapacity: waypoint?.maxCapacity ?? null,
      futureMaxCapacity: waypoint?.futureMaxCapacity ?? null,
      powerNeedItems,
    };
  });
}

export function aggregatePowerNeed(powerNeedVehicles: PowerNeedVehicle[]): { time: string; powerNeed: number }[] {
  const total: Record<string, number> = {};

  for (const vehicle of powerNeedVehicles) {
    for (const { time, powerNeed } of vehicle.powerNeedItems) {
      total[time] = (total[time] || 0) + powerNeed;
    }
  }

  return Object.entries(total).map(([time, powerNeed]) => ({ time, powerNeed }));
}

export default getPowerNeedVehicles;
